/**
 * Icons.tsx - 통합 아이콘 시스템
 * 모든 프로젝트 아이콘을 중앙 집중식으로 관리
 *
 * @description
 * - 모든 아이콘을 하나의 파일로 통합 관리
 * - Tree-shaking 지원으로 사용하지 않는 아이콘은 번들에 포함되지 않음
 * - 일관된 Props 인터페이스 제공
 */

import { memo, useEffect, useRef, useState, type CSSProperties, type ReactElement } from 'react';
import { fetchUserPhoto } from '@/shared/api/user';
import styles from './Icons.module.css';

// 아이콘 크기 타입 - 실용적 단순화
export type IconSize = 'sm' | 'md' | 'lg' | number;

// 공통 아이콘 Props 타입 - 색상 variant 지원
export interface IconProps {
  size?: IconSize;
  className?: string;
  onClick?: () => void;
  style?: CSSProperties;
  rotate?: number;
  viewBox?: string;
  variant?: IconVariant; // 색상 variant 추가

  // 접근성 (필수 유지)
  'aria-label'?: string;
  'aria-hidden'?: boolean;
  title?: string;

  // 레거시 지원 (하위 호환성)
  color?: string;
}

// 유틸리티 함수: 크기를 숫자로 변환 - 단순화
const getSizeValue = (size: IconSize | undefined, defaultSize: number): number => {
  if (typeof size === 'number') return size;
  if (typeof size === 'string') {
    // 사전 정의된 크기 매핑
    const sizeMap = { sm: 16, md: 20, lg: 24 };
    return sizeMap[size] || defaultSize;
  }
  return defaultSize;
};

// 색상 variant 타입 추가
export type IconVariant = 'default' | 'primary' | 'secondary' | 'success' | 'danger' | 'warning';

// 유틸리티 함수: CSS 클래스 생성 - 색상 variant 지원
const getIconClasses = (
  onClick?: () => void,
  className?: string,
  variant?: IconVariant,
): string => {
  const classes = [styles.icon];

  // 상호작용 가능한 아이콘만 체크
  if (onClick) {
    classes.push(styles.interactive);
  }

  // 색상 variant 적용
  if (variant && variant !== 'default' && styles[variant]) {
    classes.push(styles[variant]);
  }

  // 사용자 정의 클래스
  if (className) {
    classes.push(className);
  }

  return classes.filter(Boolean).join(' ');
};

type CreateIconPath = React.SVGProps<SVGPathElement>;

interface CreateIconText {
  x: number | string;
  y: number | string;
  fontSize: number | string;
  fill?: string;
  fontWeight?: string;
  textAnchor?: string;
  content?: string;
  children?: string;
}

interface CreateIconConfig {
  name?: string;
  viewBox: string;
  defaultSize: number;
  stroke?: string;
  fill?: string;
  strokeWidth?: number | string;
  heightRatio?: number;
  baseStyle?: CSSProperties;
  paths: CreateIconPath[];
  texts?: CreateIconText[];
}

const createIcon =
  ({
    name,
    viewBox,
    defaultSize,
    stroke,
    fill,
    strokeWidth,
    heightRatio,
    baseStyle,
    paths,
    texts,
  }: CreateIconConfig) =>
  ({
    size = defaultSize,
    className,
    style,
    onClick,
    variant,
    'aria-label': ariaLabel,
    'aria-hidden': ariaHidden,
    title,
    color,
    rotate,
  }: IconProps): ReactElement => {
    const sizeValue = getSizeValue(size, defaultSize);
    const heightValue = heightRatio ? sizeValue * heightRatio : sizeValue;
    const iconClasses = getIconClasses(onClick, className, variant);
    const defaultStroke =
      stroke ?? 'var(--icon-stroke-color, var(--color-text-secondary, #9E9E9E))';

    return (
      <svg
        width={sizeValue}
        height={heightValue}
        viewBox={viewBox}
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className={iconClasses}
        style={{
          ...(rotate !== undefined ? { transform: `rotate(${rotate}deg)` } : undefined),
          ...(color && { color }),
          ...baseStyle,
          ...style,
        }}
        onClick={onClick}
        aria-label={ariaHidden ? undefined : (ariaLabel ?? name)}
        aria-hidden={ariaHidden}
        role={onClick ? 'button' : 'img'}
        tabIndex={onClick ? 0 : undefined}
      >
        {title && <title>{title}</title>}
        {paths.map((p, idx) => (
          <path
            key={idx}
            {...p}
            stroke={
              p.stroke === undefined
                ? (color ?? defaultStroke)
                : p.stroke === 'none'
                  ? 'none'
                  : p.stroke
            }
            strokeWidth={p.strokeWidth ?? strokeWidth}
            fill={p.fill ?? fill ?? 'none'}
          />
        ))}
        {texts?.map((t, idx) => (
          <text
            key={`text-${idx}`}
            x={t.x}
            y={t.y}
            fontSize={t.fontSize}
            fill={t.fill ?? color ?? defaultStroke}
            fontWeight={t.fontWeight}
            textAnchor={(t.textAnchor as 'start' | 'middle' | 'end') ?? 'middle'}
            dominantBaseline="middle"
          >
            {t.content ?? t.children}
          </text>
        ))}
      </svg>
    );
  };

// 검색 아이콘 - 단순화된 버전
export const SearchIcon = createIcon({
  name: '검색',
  viewBox: '0 0 24 24',
  defaultSize: 14,
  strokeWidth: 1.5,
  paths: [
    {
      d: 'M19 11C19 15.4183 15.4183 19 11 19C6.58172 19 3 15.4183 3 11C3 6.58172 6.58172 3 11 3C15.4183 3 19 6.58172 19 11Z',
    },
    {
      d: 'M21 21L16.65 16.65',
    },
  ],
});

// 달력 아이콘 - 단순화된 버전
export const CalendarIcon = createIcon({
  name: '달력',
  viewBox: '0 0 24 24',
  defaultSize: 16,
  strokeWidth: 1.5,
  paths: [
    {
      d: 'M5 4H19C20.1046 4 21 4.89543 21 6V20C21 21.1046 20.1046 22 19 22H5C3.89543 22 3 21.1046 3 20V6C3 4.89543 3.89543 4 5 4Z',
    },
    {
      d: 'M16 2V6',
    },
    {
      d: 'M8 2V6',
    },
    {
      d: 'M3 10H21',
    },
  ],
});

// 기안하기 아이콘 - 문서 + 플러스
export const DraftDocumentIcon = createIcon({
  name: '기안하기',
  viewBox: '0 0 20 20',
  defaultSize: 20,
  stroke: 'currentColor',
  paths: [
    {
      d: 'M5.19336 9.5V4.92308C5.19336 4.67826 5.29683 4.44347 5.48102 4.27036C5.6652 4.09725 5.91501 4 6.17549 4H11.0862L15.9968 8.61538V15.0769C15.9968 15.3217 15.8933 15.5565 15.7092 15.7296C15.525 15.9027 15.2752 16 15.0147 16H10',
      strokeLinecap: 'round',
      strokeLinejoin: 'round',
    },
    {
      d: 'M11.0879 4V8.61538H15.9986',
      strokeLinecap: 'round',
      strokeLinejoin: 'round',
    },
    { d: 'M4 13.6914H8', strokeLinecap: 'round' },
    { d: 'M6 11.6914L6 15.6914', strokeLinecap: 'round' },
  ],
});

// @design-migration: icon-popup.svg 동기화 (기안하기 버튼용)
export const PopupArrowIcon = createIcon({
  name: '팝업',
  viewBox: '0 0 10 10',
  defaultSize: 10,
  stroke: 'currentColor',
  paths: [
    {
      d: 'M4 1H9V6',
      strokeLinecap: 'round',
      strokeLinejoin: 'round',
    },
    {
      d: 'M9 1L3 7',
      strokeLinecap: 'round',
      strokeLinejoin: 'round',
    },
  ],
});

// 플러스 아이콘 (12px, currentColor, strokeWidth 1.5)
export const PlusIcon = createIcon({
  name: '추가',
  viewBox: '0 0 12 12',
  defaultSize: 12,
  stroke: 'currentColor',
  strokeWidth: 1.5,
  paths: [
    {
      d: 'M6 1V11M1 6H11',
      strokeLinecap: 'round',
    },
  ],
});

// 플러스 원형 아이콘 - 원 안에 플러스
export const PlusCircleIcon = createIcon({
  name: '추가',
  viewBox: '0 0 11 11',
  defaultSize: 11,
  stroke: '#FF6B00',
  fill: 'white',
  paths: [
    {
      d: 'M5.25 0.375C7.94239 0.375 10.125 2.55761 10.125 5.25C10.125 7.94239 7.94239 10.125 5.25 10.125C2.55761 10.125 0.375 7.94239 0.375 5.25C0.375 2.55761 2.55761 0.375 5.25 0.375Z',
      strokeWidth: '0.75',
      strokeLinecap: 'round',
      strokeLinejoin: 'round',
      fill: 'white',
    },
    {
      d: 'M7.67352 5.24969H2.83038',
      strokeWidth: '0.75',
      strokeLinecap: 'round',
      strokeLinejoin: 'round',
    },
    {
      d: 'M5.25195 2.82812V7.67126',
      strokeWidth: '0.75',
      strokeLinecap: 'round',
      strokeLinejoin: 'round',
    },
  ],
});

// 업로드 아이콘 - 단순화된 버전
export const NewUploadIcon = createIcon({
  name: '업로드',
  viewBox: '0 0 24 24',
  defaultSize: 14,
  stroke: 'currentColor',
  strokeWidth: 2,
  paths: [
    {
      d: 'M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4',
      strokeLinecap: 'round',
      strokeLinejoin: 'round',
    },
    {
      d: 'M17 8l-5-5-5 5',
      strokeLinecap: 'round',
      strokeLinejoin: 'round',
    },
    {
      d: 'M12 3v12',
      strokeLinecap: 'round',
      strokeLinejoin: 'round',
    },
  ],
});

// 체크 완료 아이콘 - 단순화된 버전
export const CheckCompleteIcon = createIcon({
  name: '완료',
  viewBox: '0 0 14 14',
  defaultSize: 14,
  stroke: '#3ACB2D',
  paths: [
    {
      d: 'M4 8L6.05 9.64C6.10506 9.68534 6.16952 9.71784 6.23872 9.73514C6.30791 9.75244 6.38009 9.7541 6.45 9.74C6.52058 9.72676 6.58749 9.69847 6.64616 9.65706C6.70483 9.61564 6.75389 9.56207 6.79 9.5L10 4',
      strokeLinecap: 'round',
      strokeLinejoin: 'round',
    },
    {
      d: 'M7 13.5C10.5899 13.5 13.5 10.5899 13.5 7C13.5 3.41015 10.5899 0.5 7 0.5C3.41015 0.5 0.5 3.41015 0.5 7C0.5 10.5899 3.41015 13.5 7 13.5Z',
      strokeLinecap: 'round',
      strokeLinejoin: 'round',
    },
  ],
});

// 체크박스 선택 아이콘 (사각형 테두리 + 체크)
interface CheckboxBaseProps extends IconProps {
  viewBox?: string;
  viewBoxSize?: number;
  rectStroke?: string;
  rectFill?: string;
  rectRadius?: number;
  checkPath?: string;
  checkStroke?: string;
  checkStrokeWidth?: number;
  showCheck?: boolean;
}

const CheckboxIconBase = ({
  size = 14,
  className,
  style,
  onClick,
  'aria-label': ariaLabel = '체크박스',
  'aria-hidden': ariaHidden,
  viewBox = '0 0 14 14',
  viewBoxSize = 14,
  rectStroke,
  rectFill,
  rectRadius = 2.5,
  checkPath,
  checkStroke = '#FFFFFF',
  checkStrokeWidth = 2,
  showCheck = false,
  color,
}: CheckboxBaseProps): ReactElement => {
  const sizeValue = getSizeValue(size, 14);
  const iconClasses = getIconClasses(onClick, className);
  const stroke = color ?? rectStroke;

  return (
    <svg
      width={sizeValue}
      height={sizeValue}
      viewBox={viewBox}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={iconClasses}
      style={{
        ...(color && { color }),
        ...style,
      }}
      onClick={onClick}
      aria-label={ariaHidden ? undefined : ariaLabel}
      aria-hidden={ariaHidden}
      role={onClick ? 'button' : 'img'}
      tabIndex={onClick ? 0 : undefined}
    >
      {rectStroke && (
        <rect
          x="0.5"
          y="0.5"
          width={viewBoxSize - 1}
          height={viewBoxSize - 1}
          rx={rectRadius}
          fill={rectFill ?? '#ffffff'}
          stroke={stroke}
        />
      )}
      {showCheck && checkPath && (
        <path
          d={checkPath}
          stroke={checkStroke}
          strokeWidth={checkStrokeWidth}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      )}
    </svg>
  );
};

// 체크박스 선택 아이콘 (테두리 없는 체크만 표시)
export const CheckboxCheckedIcon = (props: IconProps): ReactElement =>
  CheckboxIconBase({
    size: props.size ?? 13,
    viewBox: '0 0 13 13',
    viewBoxSize: 13,
    checkPath: 'M3 6.5L5.5 9L10 4.5',
    checkStroke: props.color ?? '#FFFFFF',
    checkStrokeWidth: 1.5,
    showCheck: true,
    ...props,
  });

// 사각형 체크박스 - 테두리만 있는 기본 상태
export const CheckboxSquareIcon = (props: IconProps): ReactElement =>
  CheckboxIconBase({
    rectStroke: props.color ?? '#D9D9D9',
    rectFill: '#ffffff',
    ...props,
  });

// 사각형 체크박스 - 체크된 상태
export const CheckboxSquareCheckedIcon = (props: IconProps): ReactElement =>
  CheckboxIconBase({
    rectStroke: props.color ?? '#FF6B00',
    rectFill: props.color ?? '#FF6B00',
    checkPath: 'M4 7L6 9L10 5',
    checkStroke: '#FFFFFF',
    checkStrokeWidth: 2,
    showCheck: true,
    ...props,
  });

type ArrowDirection = 'left' | 'right';

// 화살표 공통 렌더러
const ArrowIconBase = ({
  size = 12,
  className,
  style,
  onClick,
  'aria-label': ariaLabel = '화살표',
  'aria-hidden': ariaHidden,
  color,
  direction = 'right',
  double = false,
  strokeWidth = 1,
  viewBox = '0 0 12 12',
  viewBoxWidth = 12,
  paths,
}: IconProps & {
  direction?: ArrowDirection;
  double?: boolean;
  strokeWidth?: number;
  viewBox?: string;
  viewBoxWidth?: number;
  paths?: string[];
}): ReactElement => {
  const sizeValue = getSizeValue(size, typeof size === 'number' ? size : 12);
  const iconClasses = getIconClasses(onClick, className);
  const stroke = color ?? 'currentColor';
  const transform = direction === 'left' ? `scale(-1 1) translate(-${viewBoxWidth} 0)` : undefined;

  const singlePath = 'M4.5 2L8 5.5L4.5 9';
  const doublePaths = ['M2.5 2L6 5.5L2.5 9', 'M6.5 2L10 5.5L6.5 9'];
  const renderedPaths = paths ?? (double ? doublePaths : [singlePath]);

  return (
    <svg
      width={sizeValue}
      height={sizeValue}
      viewBox={viewBox}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={iconClasses}
      style={{
        ...(color && { color }),
        ...style,
      }}
      onClick={onClick}
      aria-label={ariaHidden ? undefined : ariaLabel}
      aria-hidden={ariaHidden}
      role={onClick ? 'button' : 'img'}
      tabIndex={onClick ? 0 : undefined}
    >
      <g transform={transform}>
        {renderedPaths.map((d, idx) => (
          <path
            key={idx}
            d={d}
            stroke={stroke}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        ))}
      </g>
    </svg>
  );
};

// 좌/우/상/하 화살표 아이콘 (11~12px 기본)
export const ArrowLeftIcon = (props: IconProps): ReactElement =>
  ArrowIconBase({ direction: 'left', 'aria-label': '이전', ...props });

export const ArrowRightIcon = (props: IconProps): ReactElement =>
  ArrowIconBase({ direction: 'right', 'aria-label': '다음', ...props });

export const ArrowDoubleLeftIcon = (props: IconProps): ReactElement =>
  ArrowIconBase({ direction: 'left', double: true, 'aria-label': '첫 페이지', ...props });

export const ArrowDoubleRightIcon = (props: IconProps): ReactElement =>
  ArrowIconBase({ direction: 'right', double: true, 'aria-label': '마지막 페이지', ...props });

// 햄버거 + 화살표 토글 아이콘 (사이드바 토글 버튼)
// @design-migration: icon-1.svg 완전 동기화
export const SidebarToggleIcon = createIcon({
  name: '사이드바 토글',
  viewBox: '0 0 18 18',
  defaultSize: 18,
  strokeWidth: 1.2,
  stroke: '#9E9E9E',
  paths: [
    { d: 'M6 4L16 4', strokeLinecap: 'round' },
    { d: 'M2 14L16 14', strokeLinecap: 'round' },
    { d: 'M2 9L16 9', strokeLinecap: 'round' },
    { d: 'M2 4L4 2.5V5.5L2 4Z', fill: '#9E9E9E', stroke: 'none' },
  ],
});

// 달력/네비 화살표 (16px)
export const CalendarArrowLeftIcon = createIcon({
  name: '이전',
  viewBox: '0 0 16 16',
  defaultSize: 16,
  stroke: 'currentColor',
  paths: [
    {
      d: 'M10 4L6 8L10 12',
      strokeWidth: '2',
      strokeLinecap: 'round',
      strokeLinejoin: 'round',
    },
  ],
});

export const CalendarArrowRightIcon = createIcon({
  name: '다음',
  viewBox: '0 0 16 16',
  defaultSize: 16,
  stroke: 'currentColor',
  paths: [
    {
      d: 'M6 4L10 8L6 12',
      strokeWidth: '2',
      strokeLinecap: 'round',
      strokeLinejoin: 'round',
    },
  ],
});

// 세로 구분선 (2x230)
export const VerticalDividerIcon = ({
  height = 230,
  ...props
}: IconProps & { height?: number }): ReactElement => {
  const iconClasses = getIconClasses(props.onClick, props.className);
  const lineColor = props.color ?? '#F3F4F6';
  return (
    <svg
      width={2}
      height={height}
      viewBox={`0 0 2 ${height}`}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={iconClasses}
      style={props.style}
      onClick={props.onClick}
      aria-label={props['aria-hidden'] ? undefined : (props['aria-label'] ?? '구분선')}
      aria-hidden={props['aria-hidden']}
      role={props.onClick ? 'button' : 'img'}
      tabIndex={props.onClick ? 0 : undefined}
    >
      <path d={`M1 0V${height}`} stroke={lineColor} />
    </svg>
  );
};

// 정보 아이콘 (원형 i)
export const InfoCircleIcon = createIcon({
  name: '정보',
  viewBox: '0 0 14 14',
  defaultSize: 14,
  stroke: 'var(--color-neutral-500)',
  paths: [
    {
      d: 'M7 3.5V7.5',
      strokeLinecap: 'round',
    },
    {
      d: 'M7 10.5h0',
      strokeWidth: 2,
      strokeLinecap: 'round',
    },
    {
      d: 'M7 7m-6.5 0a6.5 6.5 0 1 0 13 0 6.5 6.5 0 1 0-13 0',
    },
  ],
});

// 첨부파일 아이콘 (문서 모양)
export const AttachmentIcon = createIcon({
  name: '첨부',
  viewBox: '0 0 24 24',
  defaultSize: 20,
  stroke: 'currentColor',
  paths: [
    {
      d: 'M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z',
      strokeWidth: '2',
      strokeLinecap: 'round',
      strokeLinejoin: 'round',
    },
    { d: 'M14 2 14 8 20 8', strokeWidth: '2', strokeLinejoin: 'round' },
  ],
});

// 상태 경고 아이콘 (제출 상태 안내)
export const StatusAlertIcon = createIcon({
  name: '경고',
  viewBox: '0 0 14 14',
  defaultSize: 14,
  stroke: '#FFA216',
  paths: [
    {
      d: 'M7 7V3.5',
      strokeLinecap: 'round',
      strokeLinejoin: 'round',
    },
    {
      d: 'M7 9C6.72386 9 6.5 9.22386 6.5 9.5C6.5 9.77614 6.72386 10 7 10C7.27614 10 7.5 9.77614 7.5 9.5C7.5 9.22386 7.27614 9 7 9Z',
      strokeLinecap: 'round',
      strokeLinejoin: 'round',
    },
    {
      d: 'M7 7m-6.5 0a6.5 6.5 0 1 0 13 0 6.5 6.5 0 1 0-13 0',
      strokeLinecap: 'round',
      strokeLinejoin: 'round',
    },
  ],
});

// 경고 삼각형 아이콘 (InfoPopup 헤더용)
export const AlertTriangleIcon = ({
  size = 24,
  className,
  onClick,
  'aria-label': ariaLabel,
  'aria-hidden': ariaHidden,
}: IconProps): ReactElement => {
  const sizeValue = getSizeValue(size, 24);
  const iconClasses = getIconClasses(onClick, className);
  return (
    <svg
      width={sizeValue}
      height={sizeValue}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      xmlns="http://www.w3.org/2000/svg"
      className={iconClasses}
      onClick={onClick}
      aria-label={ariaHidden ? undefined : (ariaLabel ?? '경고')}
      aria-hidden={ariaHidden}
      role={onClick ? 'button' : 'img'}
      tabIndex={onClick ? 0 : undefined}
    >
      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
      <line x1="12" y1="9" x2="12" y2="13" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  );
};

// 드롭다운 삼각형 (12px)
export const ChevronDownIcon = createIcon({
  name: '열기',
  viewBox: '0 0 12 12',
  defaultSize: 12,
  paths: [
    {
      d: 'M3 4.5L6 7.5L9 4.5',
      strokeLinecap: 'round',
      strokeLinejoin: 'round',
    },
  ],
});

// Small Status Dot (기본 5px)
export const StatusDotIcon = createIcon({
  name: '상태',
  viewBox: '0 0 5 5',
  defaultSize: 5,
  fill: '#FF2020',
  paths: [
    {
      d: 'M2.5 2.5m-2.5 0a2.5 2.5 0 1 0 5 0 2.5 2.5 0 1 0-5 0',
      fill: '#FF2020',
      stroke: '#FF2020',
    },
  ],
});

// Eye/Preview 아이콘 — active=true: 눈, active=false: 눈빗금 (EyeIcon 대응)
export const EyeIcon = ({
  size = 16,
  color,
  className,
  onClick,
  active = true,
  'aria-label': ariaLabel,
  'aria-hidden': ariaHidden,
}: IconProps & { active?: boolean }): ReactElement => {
  const sizeValue = getSizeValue(size, 16);
  const iconClasses = getIconClasses(onClick, className);
  return active ? (
    <svg
      width={sizeValue}
      height={sizeValue}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color ?? 'currentColor'}
      strokeWidth="2"
      xmlns="http://www.w3.org/2000/svg"
      className={iconClasses}
      onClick={onClick}
      aria-label={ariaHidden ? undefined : (ariaLabel ?? '미리보기')}
      aria-hidden={ariaHidden}
      role={onClick ? 'button' : 'img'}
      tabIndex={onClick ? 0 : undefined}
    >
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  ) : (
    <svg
      width={sizeValue}
      height={sizeValue}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color ?? 'currentColor'}
      strokeWidth="2"
      xmlns="http://www.w3.org/2000/svg"
      className={iconClasses}
      onClick={onClick}
      aria-label={ariaHidden ? undefined : (ariaLabel ?? '숨김')}
      aria-hidden={ariaHidden}
      role={onClick ? 'button' : 'img'}
      tabIndex={onClick ? 0 : undefined}
    >
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  );
};

// 연필/수정 아이콘 (ProjectInfoModal 헤더용)
export const PencilIcon = createIcon({
  name: '수정',
  viewBox: '0 0 18 18',
  defaultSize: 18,
  stroke: '#FF6B00',
  paths: [
    {
      d: 'M9.61058 11.464L5.91992 12.1283L6.53503 8.38846L13.5842 1.3639C13.6986 1.24859 13.8346 1.15707 13.9845 1.09461C14.1345 1.03216 14.2952 1 14.4577 1C14.6201 1 14.7809 1.03216 14.9308 1.09461C15.0807 1.15707 15.2167 1.24859 15.3311 1.3639L16.6351 2.66793C16.7504 2.7823 16.842 2.91836 16.9044 3.06827C16.9669 3.21819 16.999 3.37898 16.999 3.54139C16.999 3.70379 16.9669 3.86459 16.9044 4.0145C16.842 4.16441 16.7504 4.30048 16.6351 4.41484L9.61058 11.464Z',
      strokeLinecap: 'round',
      strokeLinejoin: 'round',
    },
    {
      d: 'M15.1475 12.0792V15.7699C15.1475 16.0961 15.0179 16.409 14.7872 16.6398C14.5565 16.8705 14.2436 17.0001 13.9173 17.0001H2.23022C1.90395 17.0001 1.59103 16.8705 1.36032 16.6398C1.12961 16.409 1 16.0961 1 15.7699V4.08276C1 3.75648 1.12961 3.44357 1.36032 3.21286C1.59103 2.98215 1.90395 2.85254 2.23022 2.85254H5.92088',
      strokeLinecap: 'round',
      strokeLinejoin: 'round',
    },
  ],
});

// ID 카드/프로젝트 넘버 아이콘 (14px, 회색)
export const IdCardIcon = createIcon({
  name: '프로젝트 번호',
  viewBox: '0 0 24 24',
  defaultSize: 14,
  stroke: '#9E9E9E',
  paths: [
    {
      d: 'M12 17v-2',
      strokeWidth: '2',
      strokeLinecap: 'round',
      strokeLinejoin: 'round',
    },
    {
      d: 'M12 7m-4 0a4 4 0 1 0 8 0 4 4 0 1 0-8 0',
      strokeWidth: '2',
    },
    {
      d: 'M5 11H19C20.1046 11 21 11.8954 21 13V22C21 23.1046 20.1046 24 19 24H5C3.89543 24 3 23.1046 3 22V13C3 11.8954 3.89543 11 5 11Z',
      strokeWidth: '2',
      strokeLinecap: 'round',
      strokeLinejoin: 'round',
    },
  ],
});

// Sidebar - Projects 아이콘 (@design-migration: icon-2.svg)
export const SidebarProjectsIcon = createIcon({
  name: '프로젝트',
  viewBox: '0 0 18 18',
  defaultSize: 18,
  strokeWidth: 1.2,
  paths: [
    {
      d: 'M10 2H5.5C4.94772 2 4.5 2.44772 4.5 3V9V13C4.5 13.5523 4.94772 14 5.5 14H14C14.5523 14 15 13.5523 15 13V7L10 2Z',
      strokeLinecap: 'round',
      strokeLinejoin: 'round',
    },
    {
      d: 'M10 2V7H15',
      strokeLinecap: 'round',
      strokeLinejoin: 'round',
    },
    {
      d: 'M2 5.5V14C2 15.6569 3.34315 17 5 17H11.5',
      strokeLinecap: 'round',
    },
  ],
});

// Sidebar - Approval 아이콘 (@design-migration: icon-3.svg)
export const SidebarApprovalIcon = createIcon({
  name: '전자결재',
  viewBox: '0 0 18 18',
  defaultSize: 18,
  strokeWidth: 1.2,
  paths: [
    {
      d: 'M9 16H2.5C2.22386 16 2 15.7761 2 15.5V2.5C2 2.22386 2.22386 2 2.5 2H10.5',
      strokeLinecap: 'round',
      strokeLinejoin: 'round',
    },
    {
      d: 'M12 5L13.5 2L15 5V14.5C15 14.8978 14.842 15.2794 14.5607 15.5607C14.2794 15.842 13.8978 16 13.5 16C13.1022 16 12.7206 15.842 12.4393 15.5607C12.158 15.2794 12 14.8978 12 14.5V5Z',
      strokeLinecap: 'round',
      strokeLinejoin: 'round',
    },
    {
      d: 'M12 11H15',
      strokeLinecap: 'round',
      strokeLinejoin: 'round',
    },
    {
      d: 'M5 2V16',
      strokeLinecap: 'round',
      strokeLinejoin: 'round',
    },
    {
      d: 'M7.5 5.5H9.5',
      strokeLinecap: 'round',
      strokeLinejoin: 'round',
    },
  ],
});

// Sidebar - Goals 아이콘 (@design-migration: icon-4.svg - 타겟/화살)
export const SidebarGoalsIcon = createIcon({
  name: '목표관리',
  viewBox: '0 0 18 18',
  defaultSize: 18,
  strokeWidth: 1.2,
  paths: [
    {
      d: 'M7.52791 2.95387C3.65191 2.95387 0.509792 6.09601 0.509792 9.97205C0.509792 13.8481 3.65191 16.9902 7.52791 16.9902C11.3188 16.9902 14.4077 13.9846 14.5415 10.2263M7.52791 9.97205L14.546 3.58932L14.2447 1.85618C14.1031 1.04223 13.103 0.725752 12.519 1.31011L9.74037 4.09035C9.54928 4.28155 9.44195 4.54079 9.44195 4.8111V8.21705',
      strokeLinecap: 'round',
    },
    {
      d: 'M7.52628 6.77943C5.76446 6.77943 4.33622 8.20767 4.33622 9.96948C4.33622 11.7313 5.76446 13.1595 7.52628 13.1595C9.19308 13.1595 10.5613 11.8812 10.704 10.2514',
      strokeLinecap: 'round',
    },
    {
      d: 'M10.0764 8.056L12.7442 8.72259C13.071 8.80423 13.417 8.71924 13.6687 8.49549L17.004 5.53109C17.7047 4.90825 17.2642 3.74943 16.3266 3.74943H14.5424',
      strokeLinecap: 'round',
    },
  ],
});

// 작은 위/아래 화살표 (사이드바 토글용)
export const SmallChevronIcon = createIcon({
  name: '토글',
  viewBox: '0 0 6 3',
  defaultSize: 6,
  heightRatio: 0.5,
  stroke: 'currentColor',
  paths: [
    {
      d: 'M0.5 0.5L3 2.5L5.5 0.5',
      strokeWidth: '1',
      strokeLinecap: 'round',
      strokeLinejoin: 'round',
    },
  ],
});

// 파일 닫기 아이콘 - 단순화된 버전
export const FileCloseIcon = createIcon({
  name: '닫기',
  viewBox: '0 0 14 14',
  defaultSize: 14,
  stroke: 'var(--icon-stroke-color, var(--color-text-secondary, #9E9E9E))',
  paths: [
    {
      d: 'M10.5 3.5L3.5 10.5M3.5 3.5L10.5 10.5',
      strokeWidth: '1.5',
      strokeLinecap: 'round',
      strokeLinejoin: 'round',
    },
  ],
});

// 폴더 저장 아이콘
export const FolderSaveIcon = createIcon({
  name: '폴더 저장',
  viewBox: '0 0 16 16',
  defaultSize: 16,
  stroke: 'currentColor',
  paths: [
    {
      d: 'M0.572266 13.4282V2.57108C0.572266 2.26797 0.692673 1.97728 0.907001 1.76295C1.12133 1.54863 1.41202 1.42822 1.71512 1.42822H5.93227C6.19333 1.42061 6.44913 1.50266 6.65707 1.66069C6.86501 1.81873 7.01255 2.04322 7.07512 2.29679L7.42941 3.71393H14.2866C14.5897 3.71393 14.8803 3.83434 15.0947 4.04867C15.309 4.263 15.4294 4.55369 15.4294 4.85679V13.4282C15.4294 13.7313 15.309 14.022 15.0947 14.2363C14.8803 14.4507 14.5897 14.5711 14.2866 14.5711H1.71512C1.41202 14.5711 1.12133 14.4507 0.907001 14.2363C0.692673 14.022 0.572266 13.7313 0.572266 13.4282Z',
      strokeLinecap: 'round',
      strokeLinejoin: 'round',
    },
    {
      d: 'M8.14844 7.14258V11.1426',
      strokeLinecap: 'round',
      strokeLinejoin: 'round',
    },
    {
      d: 'M6.14844 9.14258H10.1484',
      strokeLinecap: 'round',
      strokeLinejoin: 'round',
    },
  ],
});

// 사용자 프로필 아이콘 컴포넌트
// Microsoft Graph API 최소 허용 사진 크기 (48x48)
const MIN_PHOTO_SIZE = 48;

// 앱 레벨 프로필 이미지 캐시 (userId:size → Blob URL)
// 동일 userId에 대한 중복 API 호출 방지
const profileImageCache = new Map<string, Promise<string | undefined>>();

function getCachedProfileImage(userId: string, photoSize: number): Promise<string | undefined> {
  const cacheKey = `${userId}:${photoSize}`;
  const cached = profileImageCache.get(cacheKey);
  if (cached) return cached;

  const promise = fetchUserPhoto(userId, photoSize)
    .then((res) => {
      if (res.status === 200 && res.data) {
        return URL.createObjectURL(res.data);
      }
      // 204(사진 없음) 등 정상 응답은 캐시 유지 → 불필요한 재요청 방지
      return undefined;
    })
    .catch(() => {
      // 네트워크 오류 시에만 캐시 삭제 → 재시도 허용
      profileImageCache.delete(cacheKey);
      return undefined;
    });

  profileImageCache.set(cacheKey, promise);
  return promise;
}

export const UserProfileIcon = memo(
  ({
    size = 96,
    userId,
    displayName,
    alt = 'profile image',
    className,
    borderRadius = 6.6,
  }: IconProps & {
    userId?: string;
    displayName?: string;
    alt?: string;
    size?: number;
    borderRadius?: number | string;
  }): ReactElement => {
    const [src, setSrc] = useState<string>();
    const lastUrl = useRef<string | undefined>(undefined);

    // API 요청용 사진 크기 (최소 48 보장)
    const photoSize = Math.max(size, MIN_PHOTO_SIZE);

    // 프로필 이미지 로드
    // signal을 캐시 함수에 전달하지 않음: 모듈 레벨 캐시는 컴포넌트 lifecycle과 독립적이어야 함
    // (StrictMode mount→cleanup→remount 시 abort된 promise가 캐시되는 문제 방지)
    useEffect(() => {
      if (!userId) return;
      let cancelled = false;

      const loadProfileImage = async () => {
        try {
          const url = await getCachedProfileImage(userId, photoSize);

          if (cancelled) return;

          if (url) {
            lastUrl.current = url;
            setSrc(url);
          } else {
            setSrc(undefined);
          }
        } catch {
          if (!cancelled) setSrc(undefined);
        }
      };

      void loadProfileImage();

      return () => {
        cancelled = true;
      };
    }, [userId, photoSize]);

    // 문자열 → Hue 값으로 변환 (gradient seed)
    const getHueFromString = (seed: string) => {
      let h = 0x811c9dc5;
      for (let i = 0; i < seed.length; i++) {
        h ^= seed.charCodeAt(i);
        h = Math.imul(h, 0x01000193);
      }
      return Math.abs(h) % 360;
    };

    // 이니셜 생성 (최대 2글자)
    const generateInitials = (name?: string) => {
      if (!name) return '?';
      const words = name.trim().split(/\s+/);
      if (words.length === 1) {
        return words[0].charAt(0).toUpperCase();
      }
      return words
        .slice(0, 2)
        .map((w) => w.charAt(0).toUpperCase())
        .join('');
    };

    const initials = generateInitials(displayName || userId || '');
    const hue = getHueFromString(displayName || userId || '');
    const bgGradient = `linear-gradient(
    135deg,
    hsl(${hue}, 65%, 63%) 0%,
    hsl(${(hue + 20) % 360}, 65%, 47%) 100%
  )`;

    // borderRadius 값 처리 (숫자면 px 추가)
    const borderRadiusValue = typeof borderRadius === 'number' ? `${borderRadius}px` : borderRadius;

    // ✅ 이미지가 성공적으로 로드된 경우
    if (src) {
      return (
        <img
          src={src}
          width={size}
          height={size}
          alt={alt}
          loading="lazy"
          decoding="async"
          className={className ?? 'pmAvatar'}
          style={{
            borderRadius: borderRadiusValue,
            objectFit: 'cover',
            flexShrink: 0,
          }}
          onError={() => setSrc(undefined)}
        />
      );
    }

    // ✅ fallback (gradient + 이니셜)
    return (
      <div
        className={className ?? 'avatarFallback'}
        style={{
          width: size,
          height: size,
          borderRadius: borderRadiusValue,
          background: bgGradient,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#fff',
          fontSize: '16px',
          lineHeight: 1,
          userSelect: 'none',
          flexShrink: 0,
        }}
      >
        {initials}
      </div>
    );
  },
);
UserProfileIcon.displayName = 'UserProfileIcon';

// ============================================================
// 사이드바 관련 아이콘들 (Sidebar Icons)
// ============================================================

// 프로젝트 아이콘 - 원래 폴더 모양 복원
// Sidebar - 프로젝트 아이콘 (@design-migration: icon-2.svg - 겹친 문서)
export const ProjectIcon = createIcon({
  name: '프로젝트',
  viewBox: '0 0 18 18',
  defaultSize: 18,
  strokeWidth: 1.2,
  paths: [
    {
      d: 'M10 2H5.5C4.94772 2 4.5 2.44772 4.5 3V9V13C4.5 13.5523 4.94772 14 5.5 14H14C14.5523 14 15 13.5523 15 13V7L10 2Z',
      strokeLinecap: 'round',
      strokeLinejoin: 'round',
    },
    {
      d: 'M10 2V7H15',
      strokeLinecap: 'round',
      strokeLinejoin: 'round',
    },
    {
      d: 'M2 5.5V14C2 15.6569 3.34315 17 5 17H11.5',
      strokeLinecap: 'round',
    },
  ],
});

// 드롭다운 화살표 아이콘
export const DropdownArrow = createIcon({
  name: '드롭다운 화살표',
  viewBox: '0 0 10 7',
  defaultSize: 10,
  heightRatio: 0.7,
  fill: '#FF6B00',
  baseStyle: { flexShrink: 0, transition: 'transform 0.2s ease-in-out' },
  paths: [
    {
      d: 'M4.76367 6.75L0.000531787 5.64421e-08L9.52681 8.89256e-07L4.76367 6.75Z',
      fill: '#FF6B00',
      stroke: 'none',
    },
  ],
});

// 편집 아이콘
export const EditIcon = createIcon({
  name: '편집',
  viewBox: '0 0 24 24',
  defaultSize: 16,
  stroke: 'currentColor',
  strokeWidth: 1.5,
  paths: [
    {
      d: 'M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7',
      strokeLinecap: 'round',
      strokeLinejoin: 'round',
    },
    {
      d: 'M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z',
      strokeLinecap: 'round',
      strokeLinejoin: 'round',
    },
  ],
});

// 설정 아이콘 (톱니바퀴)
export const SettingsIcon = ({
  size = 16,
  variant = 'default',
  className = '',
  style,
  onClick,
  color, // 레거시 지원
}: IconProps): ReactElement => {
  const sizeValue = getSizeValue(size, 16);
  const iconClasses = getIconClasses(onClick, className, variant);

  return (
    <svg
      width={sizeValue}
      height={sizeValue}
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={iconClasses}
      style={{
        ...(color && { color }), // 레거시 color prop 지원
        ...style,
      }}
      onClick={onClick}
      aria-label="Settings Icon"
    >
      <g clipPath="url(#clip0_settings)">
        <path
          d="M5.97733 2.57129L6.46876 1.30272C6.55164 1.08793 6.69745 0.903187 6.8871 0.772674C7.07675 0.642162 7.3014 0.571969 7.53162 0.571289H8.46876C8.69898 0.571969 8.92362 0.642162 9.11327 0.772674C9.30292 0.903187 9.44874 1.08793 9.53162 1.30272L10.023 2.57129L11.6916 3.53129L13.0402 3.32557C13.2647 3.29509 13.4933 3.33206 13.6968 3.43177C13.9003 3.53148 14.0695 3.68944 14.183 3.88557L14.6402 4.68557C14.7573 4.88483 14.8113 5.11492 14.795 5.34548C14.7787 5.57605 14.6928 5.79624 14.5488 5.977L13.7145 7.03986V8.95986L14.5716 10.0227C14.7157 10.2035 14.8015 10.4237 14.8178 10.6542C14.8342 10.8848 14.7802 11.1149 14.663 11.3141L14.2059 12.1141C14.0924 12.3103 13.9231 12.4682 13.7196 12.5679C13.5161 12.6677 13.2876 12.7046 13.063 12.6741L11.7145 12.4684L10.0459 13.4284L9.55447 14.697C9.47159 14.9118 9.32578 15.0965 9.13613 15.227C8.94648 15.3576 8.72183 15.4278 8.49162 15.4284H7.53162C7.3014 15.4278 7.07675 15.3576 6.8871 15.227C6.69745 15.0965 6.55164 14.9118 6.46876 14.697L5.97733 13.4284L4.30876 12.4684L2.96019 12.6741C2.73564 12.7046 2.50709 12.6677 2.3036 12.5679C2.1001 12.4682 1.93084 12.3103 1.81733 12.1141L1.36019 11.3141C1.24305 11.1149 1.18908 10.8848 1.2054 10.6542C1.22172 10.4237 1.30757 10.2035 1.45162 10.0227L2.2859 8.95986V7.03986L1.42876 5.977C1.28471 5.79624 1.19886 5.57605 1.18254 5.34548C1.16622 5.11492 1.22019 4.88483 1.33733 4.68557L1.79447 3.88557C1.90799 3.68944 2.07724 3.53148 2.28074 3.43177C2.48423 3.33206 2.71278 3.29509 2.93733 3.32557L4.2859 3.53129L5.97733 2.57129ZM5.71447 7.99986C5.71447 8.45193 5.84853 8.89385 6.09969 9.26973C6.35084 9.64562 6.70782 9.93858 7.12548 10.1116C7.54314 10.2846 8.00272 10.3298 8.44611 10.2417C8.88949 10.1535 9.29677 9.93577 9.61643 9.6161C9.93609 9.29644 10.1538 8.88917 10.242 8.44578C10.3302 8.0024 10.2849 7.54281 10.1119 7.12515C9.93891 6.7075 9.64595 6.35052 9.27006 6.09936C8.89418 5.8482 8.45226 5.71415 8.00019 5.71415C7.39398 5.71415 6.8126 5.95496 6.38394 6.38362C5.95529 6.81227 5.71447 7.39365 5.71447 7.99986V7.99986Z"
          stroke="var(--icon-stroke-color, var(--color-text-secondary, #9E9E9E))"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </g>
      <defs>
        <clipPath id="clip0_settings">
          <rect width="16" height="16" fill="white" />
        </clipPath>
      </defs>
    </svg>
  );
};

// 삭제 아이콘
export const DeleteIcon = createIcon({
  name: '삭제',
  viewBox: '0 0 15 17',
  defaultSize: 20,
  heightRatio: 17 / 15,
  stroke: 'var(--icon-delete-color, #FF6B00)',
  paths: [
    {
      d: 'M2.05556 5.3V13.3C2.05556 15.0673 3.44845 16.5 5.16667 16.5H9.83333C11.5516 16.5 12.9444 15.0673 12.9444 13.3V5.3M9.05556 7.7V12.5M5.94444 7.7V12.5M10.6111 2.9L9.51735 1.21248C9.22885 0.767362 8.74315 0.5 8.22305 0.5H6.77695C6.25685 0.5 5.77115 0.767362 5.48265 1.21248L4.38889 2.9M10.6111 2.9H4.38889M10.6111 2.9H14.5M4.38889 2.9H0.5',
      strokeLinecap: 'round',
      strokeLinejoin: 'round',
    },
  ],
});

// 저장 아이콘
export const SaveIcon = ({
  size = 12,
  color,
  className,
  onClick,
  'aria-label': ariaLabel,
  'aria-hidden': ariaHidden,
}: IconProps): ReactElement => {
  const sizeValue = getSizeValue(size, 12);
  const iconClasses = getIconClasses(onClick, className);
  return (
    <svg
      width={sizeValue}
      height={sizeValue}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color ?? 'currentColor'}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      xmlns="http://www.w3.org/2000/svg"
      className={iconClasses}
      onClick={onClick}
      aria-label={ariaHidden ? undefined : (ariaLabel ?? '저장')}
      aria-hidden={ariaHidden}
      role={onClick ? 'button' : 'img'}
      tabIndex={onClick ? 0 : undefined}
    >
      <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
      <polyline points="17 21 17 13 7 13 7 21" />
      <polyline points="7 3 7 8 15 8" />
    </svg>
  );
};

// 드래그 핸들 아이콘 (그립 도트)
export const DragHandleIcon = createIcon({
  name: '드래그',
  viewBox: '0 0 16 16',
  defaultSize: 16,
  fill: 'currentColor',
  paths: [
    {
      d: 'M5 3h2v2H5V3zm0 4h2v2H5V7zm0 4h2v2H5v-2zm4-8h2v2H9V3zm0 4h2v2H9V7zm0 4h2v2H9v-2z',
      fill: 'currentColor',
      stroke: 'none',
    },
  ],
});

// 원형 이전/다음 화살표 아이콘 — 선형, 텍스트 색을 따른다. 512 격자 도면을 20px 안팎으로 줄이면
// 선 두께가 1px 미만~소수 px가 되어 대각선이 계단처럼 깨지므로, 선 두께를 화면 px로 고정한다
// (vector-effect: non-scaling-stroke, 1.5px).
export const ChevronCircleLeftIcon = createIcon({
  name: '이전',
  viewBox: '0 0 512 512',
  defaultSize: 20,
  stroke: 'currentColor',
  strokeWidth: 32,
  paths: [
    {
      d: 'M256 64C150 64 64 150 64 256s86 192 192 192 192-86 192-192S362 64 256 64z',
      strokeMiterlimit: 10,
      strokeWidth: 1.5,
      vectorEffect: 'non-scaling-stroke',
    },
    {
      d: 'M296 352l-96-96 96-96',
      strokeLinecap: 'round',
      strokeLinejoin: 'round',
      strokeWidth: 1.5,
      vectorEffect: 'non-scaling-stroke',
    },
  ],
});

export const ChevronCircleRightIcon = createIcon({
  name: '다음',
  viewBox: '0 0 512 512',
  defaultSize: 20,
  stroke: 'currentColor',
  strokeWidth: 32,
  paths: [
    {
      d: 'M256 64C150 64 64 150 64 256s86 192 192 192 192-86 192-192S362 64 256 64z',
      strokeMiterlimit: 10,
      strokeWidth: 1.5,
      vectorEffect: 'non-scaling-stroke',
    },
    {
      d: 'M216 352l96-96-96-96',
      strokeLinecap: 'round',
      strokeLinejoin: 'round',
      strokeWidth: 1.5,
      vectorEffect: 'non-scaling-stroke',
    },
  ],
});

// 회의실 아이콘 (문이 열린 방) — 채움형, 텍스트 색을 따른다
export const MeetingRoomIcon = createIcon({
  name: '회의실',
  viewBox: '0 0 24 24',
  defaultSize: 14,
  fill: 'currentColor',
  paths: [
    {
      d: 'M14 6v15H3v-2h2V3h9v1h5v15h2v2h-4V6h-3zm-4 5v2h2v-2h-2z',
      fill: 'currentColor',
      stroke: 'none',
    },
  ],
});

// 주차장 아이콘 (P) — 채움형, 텍스트 색을 따른다
export const ParkingIcon = createIcon({
  name: '주차장',
  viewBox: '0 0 24 24',
  defaultSize: 14,
  fill: 'currentColor',
  paths: [
    {
      d: 'M13 3H6v18h4v-6h3c3.31 0 6-2.69 6-6s-2.69-6-6-6zm.2 8H10V7h3.2c1.1 0 2 .9 2 2s-.9 2-2 2z',
      fill: 'currentColor',
      stroke: 'none',
    },
  ],
});

// 경고/주의 아이콘
export const AlertIcon = createIcon({
  name: '경고',
  viewBox: '0 0 16 16',
  defaultSize: 16,
  stroke: 'var(--icon-stroke-color, var(--color-text-secondary, #9E9E9E))',
  fill: 'currentColor',
  paths: [
    { d: 'M8 1.5a6.5 6.5 0 100 13 6.5 6.5 0 000-13z', strokeWidth: '1.5' },
    {
      d: 'M8 4.25a.75.75 0 01.75.75v3a.75.75 0 01-1.5 0v-3A.75.75 0 018 4.25z',
      fill: 'currentColor',
      stroke: 'none',
    },
    {
      d: 'M8 10.75m-0.9 0a0.9 0.9 0 1 0 1.8 0 0.9 0.9 0 1 0-1.8 0',
      fill: 'currentColor',
      stroke: 'none',
    },
  ],
});

// InfoCircleIcon을 InfoIcon 이름으로도 제공 (레거시 호환)
export const InfoIcon = InfoCircleIcon;

// 메뉴 아이콘 (햄버거 메뉴)
export const MenuIcon = createIcon({
  name: '메뉴',
  viewBox: '0 0 20 20',
  defaultSize: 20,
  stroke: 'var(--icon-stroke-color, var(--color-text-secondary, #9E9E9E))',
  paths: [{ d: 'M3 6H17M3 12H17M3 18H17', strokeWidth: '2', strokeLinecap: 'round' }],
});

// 닫기 아이콘 (X) — stroke="currentColor" (부모 color 상속)
export const CloseIcon = createIcon({
  name: '닫기',
  viewBox: '0 0 24 24',
  defaultSize: 20,
  stroke: 'currentColor',
  strokeWidth: 2,
  paths: [
    {
      d: 'M18 6L6 18M6 6L18 18',
      strokeLinecap: 'round',
    },
  ],
});

// 원형 닫기 아이콘 (주황색 테두리 원 안의 X)
export const CloseCircleIcon = createIcon({
  name: '닫기',
  viewBox: '0 0 11 11',
  defaultSize: 11,
  stroke: '#FF6B00',
  fill: 'white',
  paths: [
    {
      d: 'M5.25 0.375C7.94239 0.375 10.125 2.55761 10.125 5.25C10.125 7.94239 7.94239 10.125 5.25 10.125C2.55761 10.125 0.375 7.94239 0.375 5.25C0.375 2.55761 2.55761 0.375 5.25 0.375Z',
      strokeWidth: '0.75',
      strokeLinecap: 'round',
      strokeLinejoin: 'round',
      fill: 'white',
    },
    {
      d: 'M6.96368 3.53711L3.53906 6.96172',
      strokeWidth: '0.75',
      strokeLinecap: 'round',
      strokeLinejoin: 'round',
    },
    {
      d: 'M3.53906 3.53711L6.96368 6.96172',
      strokeWidth: '0.75',
      strokeLinecap: 'round',
      strokeLinejoin: 'round',
    },
  ],
});

// ============================================================
// 프로젝트 탭 관련 아이콘들 (Project Tab Icons)
// ============================================================

// 프로젝트 상세 수정 아이콘 - 편집/문서 형태
export const ProjectDetailEditIcon = createIcon({
  name: '프로젝트 상세 수정 아이콘',
  viewBox: '0 0 18 18',
  defaultSize: 16, // 10% 축소 (18 → 16)
  stroke: '#FF6B00',
  paths: [
    {
      d: 'M9.61058 11.464L5.91992 12.1283L6.53503 8.38846L13.5842 1.3639C13.6986 1.24859 13.8346 1.15707 13.9845 1.09461C14.1345 1.03216 14.2952 1 14.4577 1C14.6201 1 14.7809 1.03216 14.9308 1.09461C15.0807 1.15707 15.2167 1.24859 15.3311 1.3639L16.6351 2.66793C16.7504 2.7823 16.842 2.91836 16.9044 3.06827C16.9669 3.21819 16.999 3.37898 16.999 3.54139C16.999 3.70379 16.9669 3.86459 16.9044 4.0145C16.842 4.16441 16.7504 4.30048 16.6351 4.41484L9.61058 11.464Z',
      strokeLinecap: 'round',
      strokeLinejoin: 'round',
    },
    {
      d: 'M15.1475 12.0792V15.7699C15.1475 16.0961 15.0179 16.409 14.7872 16.6398C14.5565 16.8705 14.2436 17.0001 13.9173 17.0001H2.23022C1.90395 17.0001 1.59103 16.8705 1.36032 16.6398C1.12961 16.409 1 16.0961 1 15.7699V4.08276C1 3.75648 1.12961 3.44357 1.36032 3.21286C1.59103 2.98215 1.90395 2.85254 2.23022 2.85254H5.92088',
      strokeLinecap: 'round',
      strokeLinejoin: 'round',
    },
  ],
});

// 탭 간 연결 화살표 아이콘 - 단순한 오른쪽 화살표
export const TabArrowIcon = createIcon({
  name: '다음 탭',
  viewBox: '0 0 24 24',
  defaultSize: 24,
  stroke: '#C8C8C8',
  baseStyle: { flexShrink: 0 },
  paths: [
    { d: 'M8 5L16 12L8 19', strokeWidth: '2', strokeLinecap: 'round', strokeLinejoin: 'round' },
  ],
});

// 잠금 아이콘 - 탭 잠금 상태 표시
export const LockIcon = createIcon({
  name: '잠금',
  viewBox: '0 0 24 24',
  defaultSize: 12,
  stroke: '#9E9E9E',
  strokeWidth: 2,
  paths: [
    {
      d: 'M5 11H19C20.1046 11 21 11.8954 21 13V20C21 21.1046 20.1046 22 19 22H5C3.89543 22 3 21.1046 3 20V13C3 11.8954 3.89543 11 5 11Z',
    },
    {
      d: 'M7 11V7a5 5 0 0 1 10 0v4',
    },
  ],
});

// F/S 아이콘 - SVG 텍스트
// Sidebar - F&S 아이콘 (@design-migration: icon-fs.svg - 차트 트렌드라인)
export const FnsIcon = createIcon({
  name: 'F&S',
  viewBox: '0 0 18 18',
  defaultSize: 18,
  strokeWidth: 1.2,
  paths: [
    {
      d: 'M2 15L6.5 9.5L10 12L16 5',
      strokeLinecap: 'round',
      strokeLinejoin: 'round',
    },
    {
      d: 'M12.5 5H16V8.5',
      strokeLinecap: 'round',
      strokeLinejoin: 'round',
    },
  ],
});

// ============================================================
// @design-migration: Storybook 디자인 시스템 마이그레이션용 아이콘
//
// 아래 아이콘들은 Storybook 디자인에서 사용되는 lucide-react 스타일을
// 현재 createIcon 유틸로 구현한 것입니다.
// 향후 @fmg/ui-components로 마이그레이션 시 해당 패키지의
// 아이콘으로 교체 예정입니다.
// ============================================================

// Home 아이콘 (@design-migration: icon-home.svg)
export const HomeIcon = createIcon({
  name: 'HOME',
  viewBox: '0 0 18 18',
  defaultSize: 18,
  strokeWidth: 1.2,
  paths: [
    {
      d: 'M9 2L2 8V16H7V11H11V16H16V8L9 2Z',
      strokeLinecap: 'round',
      strokeLinejoin: 'round',
    },
  ],
});

// Sparkles/AI 아이콘 (Storybook: lucide-react Sparkles)
export const SparklesIcon = createIcon({
  name: 'FM AI STUDIO',
  viewBox: '0 0 22 22',
  defaultSize: 22,
  stroke: 'currentColor',
  paths: [
    {
      d: 'M12 3L13.5 7.5L18 9L13.5 10.5L12 15L10.5 10.5L6 9L10.5 7.5L12 3Z',
      strokeWidth: '1',
      strokeLinecap: 'round',
      strokeLinejoin: 'round',
    },
    {
      d: 'M5 14L5.5 16L7.5 16.5L5.5 17L5 19L4.5 17L2.5 16.5L4.5 16L5 14Z',
      strokeWidth: '1',
      strokeLinecap: 'round',
      strokeLinejoin: 'round',
    },
    {
      d: 'M17 14L17.5 16L19.5 16.5L17.5 17L17 19L16.5 17L14.5 16.5L16.5 16L17 14Z',
      strokeWidth: '1',
      strokeLinecap: 'round',
      strokeLinejoin: 'round',
    },
  ],
});

// Sidebar - F&S 아이콘 (@design-migration: icon-fs.svg - 차트)
export const SidebarFsIcon = createIcon({
  name: 'F&S',
  viewBox: '0 0 18 18',
  defaultSize: 18,
  strokeWidth: 1.2,
  paths: [
    {
      d: 'M2 15L6.5 9.5L10 12L16 5',
      strokeLinecap: 'round',
      strokeLinejoin: 'round',
    },
    {
      d: 'M12.5 5H16V8.5',
      strokeLinecap: 'round',
      strokeLinejoin: 'round',
    },
  ],
});

// ============================================
// Storybook 동기화 아이콘 (2024-12-19 추가)
// ============================================

// 아바타/사용자 아이콘
export const AvatarIcon = createIcon({
  name: '아바타',
  viewBox: '0 0 24 24',
  defaultSize: 24,
  paths: [
    {
      d: 'M12 12C14.21 12 16 10.21 16 8C16 5.79 14.21 4 12 4C9.79 4 8 5.79 8 8C8 10.21 9.79 12 12 12Z',
      fill: 'currentColor',
    },
    {
      d: 'M12 14C8.13 14 5 17.13 5 21H19C19 17.13 15.87 14 12 14Z',
      fill: 'currentColor',
    },
  ],
});

// 체크 아이콘
export const CheckIcon = createIcon({
  name: '체크',
  viewBox: '0 0 24 24',
  defaultSize: 20,
  stroke: 'currentColor',
  paths: [
    {
      d: 'M20 6L9 17L4 12',
      strokeWidth: '2',
      strokeLinecap: 'round',
      strokeLinejoin: 'round',
    },
  ],
});

// 다운로드 아이콘
export const DownloadIcon = createIcon({
  name: '다운로드',
  viewBox: '0 0 24 24',
  defaultSize: 20,
  stroke: 'currentColor',
  paths: [
    {
      d: 'M21 15V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V15',
      strokeWidth: '2',
      strokeLinecap: 'round',
      strokeLinejoin: 'round',
    },
    {
      d: 'M7 10L12 15L17 10',
      strokeWidth: '2',
      strokeLinecap: 'round',
      strokeLinejoin: 'round',
    },
    {
      d: 'M12 15V3',
      strokeWidth: '2',
      strokeLinecap: 'round',
      strokeLinejoin: 'round',
    },
  ],
});

// 문서 아이콘
export const DocumentIcon = createIcon({
  name: '문서',
  viewBox: '0 0 24 24',
  defaultSize: 20,
  stroke: 'currentColor',
  paths: [
    {
      d: 'M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z',
      strokeWidth: '2',
      strokeLinecap: 'round',
      strokeLinejoin: 'round',
    },
    {
      d: 'M14 2V8H20',
      strokeWidth: '2',
      strokeLinecap: 'round',
      strokeLinejoin: 'round',
    },
    {
      d: 'M16 13H8',
      strokeWidth: '2',
      strokeLinecap: 'round',
      strokeLinejoin: 'round',
    },
    {
      d: 'M16 17H8',
      strokeWidth: '2',
      strokeLinecap: 'round',
      strokeLinejoin: 'round',
    },
    {
      d: 'M10 9H8',
      strokeWidth: '2',
      strokeLinecap: 'round',
      strokeLinejoin: 'round',
    },
  ],
});

// 파일 아이콘 (기본)
export const FileIcon = createIcon({
  name: '파일',
  viewBox: '0 0 24 24',
  defaultSize: 20,
  stroke: 'currentColor',
  paths: [
    {
      d: 'M13 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V9L13 2Z',
      strokeWidth: '2',
      strokeLinecap: 'round',
      strokeLinejoin: 'round',
    },
    {
      d: 'M13 2V9H20',
      strokeWidth: '2',
      strokeLinecap: 'round',
      strokeLinejoin: 'round',
    },
  ],
});

// 이미지 파일 아이콘
export const ImageFileIcon = createIcon({
  name: '이미지 파일',
  viewBox: '0 0 24 24',
  defaultSize: 20,
  stroke: 'currentColor',
  paths: [
    {
      d: 'M13 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V9L13 2Z',
      strokeWidth: '2',
      strokeLinecap: 'round',
      strokeLinejoin: 'round',
    },
    {
      d: 'M13 2V9H20',
      strokeWidth: '2',
      strokeLinecap: 'round',
      strokeLinejoin: 'round',
    },
    {
      d: 'M8.5 13C9.32843 13 10 12.3284 10 11.5C10 10.6716 9.32843 10 8.5 10C7.67157 10 7 10.6716 7 11.5C7 12.3284 7.67157 13 8.5 13Z',
      strokeWidth: '2',
      strokeLinecap: 'round',
      strokeLinejoin: 'round',
    },
    {
      d: 'M17 19L13 15L7 21',
      strokeWidth: '2',
      strokeLinecap: 'round',
      strokeLinejoin: 'round',
    },
  ],
});

// PDF 파일 아이콘
export const PdfFileIcon = createIcon({
  name: 'PDF 파일',
  viewBox: '0 0 24 24',
  defaultSize: 20,
  stroke: 'currentColor',
  paths: [
    {
      d: 'M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z',
      strokeWidth: '2',
      strokeLinecap: 'round',
      strokeLinejoin: 'round',
      fill: 'none',
    },
    {
      d: 'M14 2V8H20',
      strokeWidth: '2',
      strokeLinecap: 'round',
      strokeLinejoin: 'round',
    },
  ],
  texts: [
    {
      content: 'PDF',
      x: '12',
      y: '16',
      fontSize: '5',
      fontWeight: 'bold',
      textAnchor: 'middle',
      fill: 'currentColor',
    },
  ],
});

// Excel 파일 아이콘
export const ExcelFileIcon = createIcon({
  name: 'Excel 파일',
  viewBox: '0 0 24 24',
  defaultSize: 20,
  stroke: 'currentColor',
  paths: [
    {
      d: 'M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z',
      strokeWidth: '2',
      strokeLinecap: 'round',
      strokeLinejoin: 'round',
      fill: 'none',
    },
    {
      d: 'M14 2V8H20',
      strokeWidth: '2',
      strokeLinecap: 'round',
      strokeLinejoin: 'round',
    },
    {
      d: 'M8 12H16',
      strokeWidth: '1.5',
      strokeLinecap: 'round',
    },
    {
      d: 'M8 15H16',
      strokeWidth: '1.5',
      strokeLinecap: 'round',
    },
    {
      d: 'M8 18H16',
      strokeWidth: '1.5',
      strokeLinecap: 'round',
    },
    {
      d: 'M11 12V18',
      strokeWidth: '1.5',
      strokeLinecap: 'round',
    },
  ],
});

// ============================================
// 토글 아이콘 (상태 기반)
// ============================================

/**
 * EyeToggleIcon - 활성/비활성 토글이 가능한 눈 아이콘
 * CalendarCard의 필터 토글 버튼 등에 사용
 *
 * @param active - true: 눈 열림 (보이기), false: 눈 닫힘 (숨기기)
 */
export interface EyeToggleIconProps extends Omit<IconProps, 'variant'> {
  active?: boolean;
}

export const EyeToggleIcon = ({
  active = true,
  size = 16,
  className = '',
  'aria-label': ariaLabel,
  ...props
}: EyeToggleIconProps): ReactElement => {
  const sizeValue = getSizeValue(size, 16);

  return active ? (
    <svg
      className={className}
      width={sizeValue}
      height={sizeValue}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-label={ariaLabel ?? '보이기'}
      role="img"
      {...props}
    >
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  ) : (
    <svg
      className={className}
      width={sizeValue}
      height={sizeValue}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-label={ariaLabel ?? '숨기기'}
      role="img"
      {...props}
    >
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  );
};

// ============================================
// 별칭(Alias) Exports
// ============================================

// TrashIcon은 DeleteIcon과 동일
export const TrashIcon = DeleteIcon;

// UploadIcon은 NewUploadIcon과 동일
export const UploadIcon = NewUploadIcon;

// 방향 화살표 별칭 (Storybook 호환)
export const ChevronLeftIcon = ArrowLeftIcon;
export const ChevronRightIcon = ArrowRightIcon;

// ============================================
// 조직관리용 아이콘 (HRup Organization)
// ============================================

// 폴더 아이콘 (닫힌 상태)
export const FolderIcon = createIcon({
  name: '폴더',
  viewBox: '0 0 24 24',
  defaultSize: 18,
  stroke: 'currentColor',
  paths: [
    {
      d: 'M4 6h5l2 2h9a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1z',
      strokeWidth: '1.5',
      strokeLinecap: 'round',
      strokeLinejoin: 'round',
    },
  ],
});

// 폴더 아이콘 (열린 상태)
export const FolderOpenIcon = createIcon({
  name: '열린 폴더',
  viewBox: '0 0 24 24',
  defaultSize: 18,
  stroke: 'currentColor',
  paths: [
    {
      d: 'M4 6h5l2 2h9a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1z',
      strokeWidth: '1.5',
      strokeLinecap: 'round',
      strokeLinejoin: 'round',
    },
  ],
});

// 별 아이콘 (부서장 표시용)
export const StarIcon = createIcon({
  name: '별',
  viewBox: '0 0 20 20',
  defaultSize: 20,
  stroke: 'currentColor',
  paths: [
    {
      d: 'M10 2L12.09 6.26L16.84 6.97L13.42 10.29L14.18 15.02L10 12.77L5.82 15.02L6.58 10.29L3.16 6.97L7.91 6.26L10 2Z',
      strokeWidth: '1.5',
      strokeLinecap: 'round',
      strokeLinejoin: 'round',
    },
  ],
});

// 채워진 별 아이콘 (부서장 표시용) - currentColor로 부모 색상 상속
export const StarFilledIcon = createIcon({
  name: '별',
  viewBox: '0 0 20 20',
  defaultSize: 20,
  fill: 'currentColor',
  stroke: 'currentColor',
  paths: [
    {
      d: 'M10 2L12.09 6.26L16.84 6.97L13.42 10.29L14.18 15.02L10 12.77L5.82 15.02L6.58 10.29L3.16 6.97L7.91 6.26L10 2Z',
      strokeWidth: '1.5',
      strokeLinecap: 'round',
      strokeLinejoin: 'round',
      fill: 'currentColor',
    },
  ],
});

// 게시글 상단 고정 아이콘
export const PinIcon = createIcon({
  name: '핀',
  viewBox: '0 0 24 24',
  defaultSize: 16,
  stroke: 'currentColor',
  strokeWidth: 2,
  paths: [
    {
      d: 'M12 17v5',
      strokeLinecap: 'round',
      strokeLinejoin: 'round',
    },
    {
      d: 'M9 10.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24V16a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V7a1 1 0 0 1 1-1 2 2 0 0 0 0-4H8a2 2 0 0 0 0 4 1 1 0 0 1 1 1z',
      strokeLinecap: 'round',
      strokeLinejoin: 'round',
    },
  ],
});

// 사용자 아이콘 (단순 형태)
export const UserIcon = createIcon({
  name: '사용자',
  viewBox: '0 0 20 20',
  defaultSize: 20,
  stroke: 'currentColor',
  paths: [
    {
      d: 'M10 10C12.21 10 14 8.21 14 6C14 3.79 12.21 2 10 2C7.79 2 6 3.79 6 6C6 8.21 7.79 10 10 10Z',
      strokeWidth: '1.5',
      strokeLinecap: 'round',
      strokeLinejoin: 'round',
    },
    {
      d: 'M3 18C3 14.69 6.13 12 10 12C13.87 12 17 14.69 17 18',
      strokeWidth: '1.5',
      strokeLinecap: 'round',
      strokeLinejoin: 'round',
    },
  ],
});

// 사용자들 아이콘 (부서원 표시용)
export const UsersIcon = createIcon({
  name: '사용자들',
  viewBox: '0 0 20 20',
  defaultSize: 20,
  stroke: 'currentColor',
  paths: [
    {
      d: 'M14 18V16.5C14 14.0147 11.7614 12 9 12H6C3.23858 12 1 14.0147 1 16.5V18',
      strokeWidth: '1.5',
      strokeLinecap: 'round',
      strokeLinejoin: 'round',
    },
    {
      d: 'M7.5 9C9.433 9 11 7.433 11 5.5C11 3.567 9.433 2 7.5 2C5.567 2 4 3.567 4 5.5C4 7.433 5.567 9 7.5 9Z',
      strokeWidth: '1.5',
      strokeLinecap: 'round',
      strokeLinejoin: 'round',
    },
    {
      d: 'M19 18V16.5C18.9986 15.5174 18.5645 14.5852 17.8165 13.9504C17.0684 13.3156 16.0799 13.0371 15.1 13.182',
      strokeWidth: '1.5',
      strokeLinecap: 'round',
      strokeLinejoin: 'round',
    },
    {
      d: 'M13 2.18201C13.9831 2.33571 14.8752 2.86968 15.4887 3.68252C16.1022 4.49536 16.3885 5.52512 16.2867 6.55203C16.1849 7.57895 15.7026 8.52971 14.9352 9.21015C14.1679 9.8906 13.1718 10.2504 12.15 10.218',
      strokeWidth: '1.5',
      strokeLinecap: 'round',
      strokeLinejoin: 'round',
    },
  ],
});

// 빌딩/조직 아이콘
export const BuildingIcon = createIcon({
  name: '빌딩',
  viewBox: '0 0 20 20',
  defaultSize: 20,
  stroke: 'currentColor',
  paths: [
    {
      d: 'M3 18V4C3 3.46957 3.21071 2.96086 3.58579 2.58579C3.96086 2.21071 4.46957 2 5 2H11C11.5304 2 12.0391 2.21071 12.4142 2.58579C12.7893 2.96086 13 3.46957 13 4V18',
      strokeWidth: '1.5',
      strokeLinecap: 'round',
      strokeLinejoin: 'round',
    },
    {
      d: 'M13 8H15C15.5304 8 16.0391 8.21071 16.4142 8.58579C16.7893 8.96086 17 9.46957 17 10V18',
      strokeWidth: '1.5',
      strokeLinecap: 'round',
      strokeLinejoin: 'round',
    },
    {
      d: 'M6 6H10',
      strokeWidth: '1.5',
      strokeLinecap: 'round',
    },
    {
      d: 'M6 10H10',
      strokeWidth: '1.5',
      strokeLinecap: 'round',
    },
    {
      d: 'M6 14H10',
      strokeWidth: '1.5',
      strokeLinecap: 'round',
    },
    {
      d: 'M1 18H19',
      strokeWidth: '1.5',
      strokeLinecap: 'round',
    },
  ],
});

// 화살표 위 아이콘 (상위 부서 이동용)
export const ArrowUpIcon = createIcon({
  name: '위로',
  viewBox: '0 0 20 20',
  defaultSize: 20,
  stroke: 'currentColor',
  paths: [
    {
      d: 'M10 16V4M10 4L5 9M10 4L15 9',
      strokeWidth: '1.5',
      strokeLinecap: 'round',
      strokeLinejoin: 'round',
    },
  ],
});

// 화살표 아래 아이콘 (하위 부서 이동용)
export const ArrowDownIcon = createIcon({
  name: '아래로',
  viewBox: '0 0 20 20',
  defaultSize: 20,
  stroke: 'currentColor',
  paths: [
    {
      d: 'M10 4V16M10 16L5 11M10 16L15 11',
      strokeWidth: '1.5',
      strokeLinecap: 'round',
      strokeLinejoin: 'round',
    },
  ],
});

// 히스토리/시계 아이콘
export const HistoryIcon = createIcon({
  name: '히스토리',
  viewBox: '0 0 20 20',
  defaultSize: 20,
  stroke: 'currentColor',
  paths: [
    {
      d: 'M10 18C14.4183 18 18 14.4183 18 10C18 5.58172 14.4183 2 10 2C5.58172 2 2 5.58172 2 10C2 14.4183 5.58172 18 10 18Z',
      strokeWidth: '1.5',
      strokeLinecap: 'round',
      strokeLinejoin: 'round',
    },
    {
      d: 'M10 5V10L13 13',
      strokeWidth: '1.5',
      strokeLinecap: 'round',
      strokeLinejoin: 'round',
    },
  ],
});

// 리프레시/새로고침 아이콘
export const RefreshIcon = createIcon({
  name: '새로고침',
  viewBox: '0 0 20 20',
  defaultSize: 20,
  stroke: 'currentColor',
  paths: [
    {
      d: 'M17 3V7H13',
      strokeWidth: '1.5',
      strokeLinecap: 'round',
      strokeLinejoin: 'round',
    },
    {
      d: 'M3 17V13H7',
      strokeWidth: '1.5',
      strokeLinecap: 'round',
      strokeLinejoin: 'round',
    },
    {
      d: 'M4.05 8C4.33 6.83 4.92 5.77 5.74 4.93C6.56 4.09 7.59 3.49 8.73 3.2C9.87 2.91 11.07 2.94 12.19 3.29C13.31 3.64 14.32 4.29 15.1 5.17L17 7M3 13L4.9 14.83C5.68 15.71 6.69 16.36 7.81 16.71C8.93 17.06 10.13 17.09 11.27 16.8C12.41 16.51 13.44 15.91 14.26 15.07C15.08 14.23 15.67 13.17 15.95 12',
      strokeWidth: '1.5',
      strokeLinecap: 'round',
      strokeLinejoin: 'round',
    },
  ],
});

// HRup 아이콘 (인사관리)
export const HRupIcon = createIcon({
  name: 'HRup',
  viewBox: '0 0 18 18',
  defaultSize: 18,
  stroke: 'currentColor',
  paths: [
    {
      d: 'M9 8C10.6569 8 12 6.65685 12 5C12 3.34315 10.6569 2 9 2C7.34315 2 6 3.34315 6 5C6 6.65685 7.34315 8 9 8Z',
      strokeWidth: '1.2',
      strokeLinecap: 'round',
    },
    {
      d: 'M3 16C3 12.6863 5.68629 10 9 10C12.3137 10 15 12.6863 15 16',
      strokeWidth: '1.2',
      strokeLinecap: 'round',
    },
  ],
});
